import type FinishedResponseInteractionService from '../commons/services/finished-response-interaction.service';
import type {IOnDestroy, IOnInit} from 'angular';
import bind from 'bind-decorator';
import {remove} from 'shared/utils/array-utils.fp';
import type {Category, Language, Localization, Translation} from 'typings/portaro.be.types';
import type LocalizationsService from '../commons/services/localizations.service';
import type {UrlStateManagerService} from '../commons/services/url-state-manager.service';
import type {LocalizationsSearchParams} from '../commons/data-services/localizations-data.service';

/**
 * @ngdoc component
 * @name kpLocalizations
 * @module portaro.components
 *
 *
 * @description
 */
export default class KpLocalizationsComponent {
    public static componentName = 'kpLocalizations';

    public templateUrl = require('./kp-localizations.tpl.ftl');
    public controller = KpLocalizationsComponentController;
}

class KpLocalizationsComponentController implements IOnInit, IOnDestroy {

    public categories: Category[];
    public searchQuery: string;
    public languages: Language[];
    public activeCategories = {};
    public messages: Localization[] = null;
    private deregisterUrlChangeHandler: () => void;

    /*@ngInject*/
    protected constructor(private localizationsService: LocalizationsService,
                          private finishedResponseInteractionService: FinishedResponseInteractionService,
                          private urlStateManagerService: UrlStateManagerService) {
    }


    public async $onInit(): Promise<void> {
        await this.loadCategories();
        await this.loadLanguages();
        this.deregisterUrlChangeHandler = this.urlStateManagerService.register((params: LocalizationsSearchParams) => {
            if (this.searchQuery !== params.q) {
                this.searchQuery = params.q;
            }
            const promise = Object.keys(params).length === 0 ? Promise.resolve([]) : this.localizationsService.query(params);
            return promise.then(this.showMessages);
        });
    }

    public $onDestroy(): void {
        this.deregisterUrlChangeHandler();
    }

    public async createNewMessage(): Promise<void> {
        const response = await this.localizationsService.createNewMessage({});
        this.addNewMessage(response);
    }

    public async editMessage(message: Localization): Promise<void> {
        const response = await this.localizationsService.editMessage({...this.convertLocalizationForServer(message), confirmed: false});
        this.addNewMessage(response);
    }

    public async deleteMessage(message: Localization): Promise<void> {
        await this.localizationsService.deleteMessage(message);
        const removeMessageFromGivenArray = remove(message);
        removeMessageFromGivenArray(this.messages);
    }

    private convertLocalizationForServer(localization: Localization): any {
        const convertedLocalization = {} as any;
        convertedLocalization.code = localization.code;
        convertedLocalization.department = localization.depId;

        Object.keys(localization.translations).forEach((key) => convertedLocalization[key] = localization.translations[key]?.value);

        return convertedLocalization;
    }


    private async loadCategories(): Promise<void> {
        this.categories = await this.localizationsService.getCategories();
    }

    private async loadLanguages(): Promise<void> {
        this.languages = await this.localizationsService.getLanguages();
    }

    public async deleteTranslation(translation: Translation): Promise<void> {
        await this.localizationsService.deleteTranslation(translation);
        translation.value = null;
    }

    public showCategory(category: Category): void {
        this.urlStateManagerService.requestChangeState({category: category.id});
    }

    public search(query: string): void {
        if (query.length > 0) {
            this.urlStateManagerService.requestChangeState({q: query});
        }
    }

    public getTranslationsByLanguages(message: Localization, languages: Language[]): Translation[] {
        return languages.map((lang) => {
            return message.translations[lang.id];
        });
    }

    @bind
    private showMessages(messages: Localization[]): Localization[] {
        this.deactivateAllCategories();
        this.messages = messages;
        this.messages.forEach(this.convertLoadedMessage);
        return messages;
    }

    @bind
    private convertLoadedMessage(message: Localization): void {
        const category = KpLocalizationsComponentController.getCategoryFromMessageCode(message.code);
        this.activeCategories[category] = true;
        this.languages.forEach((language) => {
            const languageId = language.id;
            const translation = message.translations[languageId];
            message.translations[languageId] = {
                code: message.code,
                depId: message.depId,
                language: languageId,
                value: translation,
            };
        });
    }

    private deactivateAllCategories(): void {
        this.activeCategories = {};
    }

    private static getCategoryFromMessageCode(mc: string): string {
        return mc.split('.', 1)[0];
    }

    private addNewMessage(response: Localization) {
        this.convertLoadedMessage(response);
        this.messages = this.messages.filter((element) => {
            return !(element.depId === response.depId && element.code === response.code);
        })
        this.messages.push(response);
        this.messages.sort((a, b) => a.code.localeCompare(b.code));
    }
}
